import React, { useState, useEffect, useCallback, SyntheticEvent } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  AlertColor,
  CircularProgress,
  Container,
  Snackbar,
  Toolbar,
  Stack,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  AccessTime as AccessTimeIcon,
  Hotel as HotelIcon,
  Spa as SpaIcon,
  Person as PersonIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  DirectionsCar as DirectionsCarIcon,
} from '@mui/icons-material';
import { toCastOrderView } from '../../features/cast/castOrderUtils';
import { format, startOfWeek, endOfWeek, startOfDay, endOfDay, parse, addDays, setHours, setMinutes, setSeconds, parseISO, isValid, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { Theme } from '@mui/material/styles';
import { GridRowParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid';
import axios from 'axios';
import { ja } from 'date-fns/locale';

import AuthService from '../../services/auth';
import { orderService, Order as ServiceOrder } from '../../services/orderService';
import { getCustomers } from '../../services/customer';
import userService from '../../services/userService';
import { User } from '../../types/user';
import PageHeader from '../../components/layout/PageHeader';
import DashboardLayout from '../../components/layout/DashboardLayout';
import '../../styles/OrderManagement.css';
import api from '../../services/apiClient';
import config from '../../config';
import { getCourses } from '../../services/settings';
import { getAdjustedDate, formatDateTimeForServer } from '../../utils/dateUtils';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import { useBusinessDate } from '../../hooks/useBusinessDate';
import BusinessDatePicker from '../../components/common/BusinessDatePicker';

// 型定義
// orderService.tsからインポートした型を使用
type Order = ServiceOrder;

interface Customer {
  id: string | number;
  name: string;
  phone?: string;
}

interface Cast {
  id: number | string;
  first_name: string;
  last_name: string;
  username?: string; // デバッグ用
}

interface Shift {
  id: number;
  date: string;
  user_id: number;
  start_time?: string;
  end_time?: string;
  break_start?: string;
  break_end?: string;
}

interface NewOrder {
  customer_id: string | number;
  user_id: string | number; // キャスト
  driver_id?: string | number; // 担当ドライバー
  order_number?: string;
  status: string; // 予約、完了、チェンジ、キャンセル
  description: string;
  order_type: 'free' | 'nomination' | 'direct_nomination'; // フリー・指名・本指名
  pickup_address: string; // 送迎
  delivery_address: string; // ホテル
  pickup_date: string; // 予約日
  delivery_date: string; // 予約時間
  entry_time?: string; // 入室時間
  course: string; // コース
  options?: string[]; // オプション
  discount?: number; // 割引額
  special_instructions?: string;
}

// CourseItem型を定義（settings.tsと一致させる）
interface CourseItem {
  id: string;
  duration: number;
  castback: number;
  revenue: number;
  name?: string; // nameをオプショナルに変更
  is_default?: boolean;
}

const OrderManagement = () => {
  const navigate = useNavigate();
  const { allBusinessHours, loading: settingsLoading } = useSystemSettings();
  const [orders, setOrders] = useState<Order[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [casts, setCasts] = useState<Cast[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]); // ドライバー一覧
  const [availableOptions, setAvailableOptions] = useState<string[]>([]); // 利用可能なオプション一覧
  const [availableCourses, setAvailableCourses] = useState<string[]>(['60分', '90分', '120分']); // コース一覧
  const [coursesData, setCoursesData] = useState<CourseItem[]>([]); // システム設定から取得したコース情報
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dateViewMode, setDateViewMode] = useState<'daily' | 'weekly'>('daily');
  const [selectedCast, setSelectedCast] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('info');
  const [selectedCastOptions, setSelectedCastOptions] = useState<string[]>([]); // 選択されたキャストのオプション
  const [newOrder, setNewOrder] = useState<NewOrder>({
    customer_id: '',
    user_id: '',
    driver_id: '',
    status: '予約',
    description: '',
    order_type: 'free',
    pickup_address: '',
    delivery_address: '',
    pickup_date: format(new Date(), 'yyyy-MM-dd'),
    delivery_date: format(new Date(), 'HH:mm'),
    entry_time: format(new Date(), 'HH:mm'),
    course: '60分',
    options: [],
    discount: 0,
    special_instructions: ''
  });

  // ページネーションの状態を管理
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // 追加の状態変数
  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');
  // カード一覧のソート基準（予約時間 / 名前 / 退出時間）と昇降順
  const [cardSortKey, setCardSortKey] = useState<'reservation' | 'name' | 'exit' | 'status'>('reservation');
  const [cardSortDir, setCardSortDir] = useState<'asc' | 'desc'>('asc');
  const [timeRange, setTimeRange] = useState<{start: number, end: number}>({start: 10, end: 27}); // 10:00～27:00（翌日3:00）

  const { businessDate, isLoading: isBusinessDateLoading } = useBusinessDate();

  useEffect(() => {
    // isBusinessDateLoadingが完了し、businessDateが有効ならselectedDateの初期値として設定
    if (!isBusinessDateLoading && businessDate) {
      setSelectedDate(businessDate);
    }
  }, [businessDate, isBusinessDateLoading]);

  // 営業時間取得失敗時にローディングを停止し、エラーを表示する
  useEffect(() => {
    // settingsLoadingが完了し、かつallBusinessHoursが存在しない場合にエラー処理
    if (!settingsLoading && !allBusinessHours) {
      setIsLoading(false);
      setError('営業時間設定が読み込めませんでした。オーダーを取得できません。');
    }
  }, [settingsLoading, allBusinessHours]);

  // カスタムスナックバー表示関数
  const showSnackbar = useCallback((message: string, variant: AlertColor = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(variant);
    setSnackbarOpen(true);
  }, []);

  // 新規オーダーページに遷移する関数
  const navigateToCreateOrder = useCallback(() => {
    navigate('/orders/create');
  }, [navigate]);

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // ユーザーロールをチェックする関数
  const checkUserPermission = useCallback(() => {
    // ローカルストレージからユーザー情報を取得
    const userStr = localStorage.getItem('user');
    
    if (!userStr) {
      showSnackbar('ログインしてください', 'error');
      navigate('/login');
      return false;
    }

    const userData = JSON.parse(userStr);
    console.log('OrderManagement - User Data:', userData);

    const userRole = (userData.user?.role || userData.role || '').toUpperCase();
    console.log('OrderManagement - User Role:', userRole);

    // オーダー管理の閲覧を許可するロール。
    // CAST/DRIVER はバックエンド側で自分が担当するオーダーのみに絞り込まれる。
    const hasPermission = ['ADMIN', 'MANAGER', 'DISPATCHER', 'DRIVER', 'CAST'].includes(userRole);

    if (!hasPermission) {
      showSnackbar('オーダー管理を行う権限がありません', 'warning');
      navigate('/');
      return false;
    }

    setCurrentUser(userData.user || userData);
    return true;
  }, [navigate, showSnackbar]);

  // 初回レンダリング時にユーザー情報を取得
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      const userRole = (userData.user?.role || userData.role || '').toLowerCase();
      console.log('OrderManagement - Initial User Role:', userRole);
      setCurrentUser(userData.user || userData);
    }
  }, []);

  // 初期表示時に現在の日付を営業時間に基づいて調整
  useEffect(() => {
    const initializeDate = async () => {
      try {
        // 営業時間に基づいた調整はloadOrdersで行うため、ここでは単純に現在の日付をセット
        setSelectedDate(new Date());
      } catch (error) {
        console.error('日付初期化エラー:', error);
        // エラー時は現在の日付をそのまま使用
        setSelectedDate(new Date());
      }
    };
    
    initializeDate();
  }, []);

  useEffect(() => {
    const loadOrders = async () => {
      // このuseEffectはfetchOrdersに統合するため、処理を空にする
    };

    // loadOrders(); // 呼び出しをコメントアウト
  }, [selectedDate, dateViewMode, selectedCast, allBusinessHours, settingsLoading]);

  // 顧客・キャスト・コースなどの初期データを取得
  useEffect(() => {
    const initialFetch = async () => {
      if (!checkUserPermission()) return;
      
      // setIsLoading(true); // settingsLoadingを主ローディングとするためコメントアウト
      try {
        await Promise.all([
          fetchSystemSettings(),
          fetchShiftsData()
        ]);
      } catch (error) {
        console.error("初期データの取得中にエラーが発生しました:", error);
        setError("初期データの読み込みに失敗しました。");
      }
    };
    initialFetch();
  }, []); // 空の依存配列でマウント時に一度だけ実行



  // スナックバーを閉じる
  const handleSnackbarClose = (_event: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // オーダー編集を開始する
  const handleEditOrder = (order: Order) => {
    // バックエンドから返されるデータを正しくマッピング
    const mappedOrder = {
      ...order,
      // 予約日時を日付と時間に分割
      pickup_date: order.reservation_datetime 
        ? new Date(order.reservation_datetime).toISOString().split('T')[0] 
        : '',
      delivery_date: order.reservation_time || '',
      reservation_time: order.reservation_time || '',
      // コース名をマッピング
      course: order.course_name || order.course || '60分',
      // 不足しているフィールドのデフォルト値
      discount: order.discount || 0,
      service_time: order.service_time || 0,
      // option_detailsの安全な処理
      option_details: (() => {
        try {
          if (!order.option_details) return JSON.stringify([]);
          if (typeof order.option_details === 'string') {
            // 既にJSON文字列の場合はそのまま使用
            const parsed = JSON.parse(order.option_details);
            return JSON.stringify(parsed);
          }
          // オブジェクトの場合はJSON文字列に変換
          return JSON.stringify(order.option_details);
        } catch (error) {
          console.error('option_detailsの処理中にエラー:', error);
          return JSON.stringify([]);
        }
      })()
    };
    
    setSelectedOrder(mappedOrder);
  };

  // オーダー編集ダイアログを開く（handleEditOrderの別名）
  const openEditOrderDialog = (order: Order) => {
    handleEditOrder(order);
  };

  // オーダーのステータスを変更する関数
  const handleOrderStatusChange = async (orderId: number | string, newStatus: string) => {
    try {
      // IDを数値に変換
      const numericId = typeof orderId === 'string' ? parseInt(orderId, 10) : orderId;
      
      // orderServiceを使用して更新
      await orderService.updateOrder(numericId, { status: newStatus });

      // 成功したらオーダー一覧を再取得して最新情報を表示
      if (selectedDate) {
        await fetchOrders(selectedDate);
      }
      
      // ステータスに応じたメッセージを表示
      let message = 'オーダーの状態を更新しました';
      if (newStatus === 'completed' || newStatus === '完了') {
        message = 'オーダーを完了にしました';
      } else if (newStatus === 'cancelled' || newStatus === 'キャンセル') {
        message = 'オーダーをキャンセルにしました';
      } else if (newStatus === 'changed' || newStatus === 'チェンジ') {
        message = 'オーダーをチェンジにしました';
      }
      
      showSnackbar(message, 'success');
    } catch (err) {
      console.error('オーダーステータス更新エラー:', err);
      showSnackbar('オーダーの状態更新に失敗しました', 'error');
    }
  };

  // オーダーを完了にする
  const handleOrderComplete = async (orderId: number | string) => {
    await handleOrderStatusChange(orderId, 'completed');
  };

  // 新規オーダーを作成する
  const handleCreateOrder = async () => {
    try {
      // 必須項目のバリデーション
      if (!newOrder.customer_id || !newOrder.delivery_address) {
        showSnackbar('顧客と配達先は必須です', 'error');
        return;
      }

      // orderServiceを使用して作成
      const createdOrder = await orderService.createOrder(newOrder);
      
      // 成功したらダイアログを閉じて一覧を更新
      setIsCreateDialogOpen(false);
      setOrders([createdOrder, ...orders]);
      
      // 新規オーダーをリセット
      setNewOrder({
        customer_id: '',
        user_id: '',
        driver_id: '',
        status: '予約',
        description: '',
        order_type: 'free',
        pickup_address: '',
        delivery_address: '',
        pickup_date: format(new Date(), 'yyyy-MM-dd'),
        delivery_date: format(new Date(), 'HH:mm'),
        entry_time: format(new Date(), 'HH:mm'),
        course: '60分',
        options: [],
        discount: 0,
        special_instructions: ''
      });
      
      showSnackbar('オーダーを作成しました', 'success');
    } catch (err) {
      console.error('オーダー作成エラー:', err);
      showSnackbar('オーダーの作成に失敗しました', 'error');
    }
  };

  // 日付・時刻フォーマット関数
  const formatDateOnly = (isoString: string | null | undefined): string => {
    if (!isoString) return '';
    // ISO形式から日付だけを抽出する（YYYY-MM-DD形式）
    return isoString.split('T')[0];
  };

  const formatTimeOnly = (isoString: string | null | undefined): string => {
    if (!isoString) return '';
    // すでに HH:MM(:SS) 形式の文字列はそのまま時:分を返す（new Date("18:30")は不正になるため）
    const hhmm = String(isoString).match(/^(\d{1,2}):(\d{2})/);
    if (hhmm) {
      return `${hhmm[1].padStart(2, '0')}:${hhmm[2]}`;
    }
    try {
      // ISO形式から時:分だけを抽出する
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return date.getHours().toString().padStart(2, '0') + ':' + 
             date.getMinutes().toString().padStart(2, '0');
    } catch (e) {
      console.error('時刻変換エラー:', e);
      return '';
    }
  };

  // 時刻文字列をバックエンド送信用に変換（SQLite対応形式）
  const combineDateTime = (dateStr: string, timeStr: string): string => {
    if (!dateStr || !timeStr) {
      console.log('日付または時間が未設定です', { dateStr, timeStr });
      return '';
    }
    
    try {
      console.log('日時結合の入力:', { dateStr, timeStr });
      
      // 入力された時間が24時以降の場合（深夜営業用）
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // 時間が数値に変換できない場合はエラー
      if (isNaN(hours) || isNaN(minutes)) {
        console.error('時間のフォーマットが不正です:', timeStr);
        return '';
      }
      
      let finalDate = dateStr;
      let adjustedHours = hours;
      
      // 24時以降の場合は日付を1日進め、時間を0-23の範囲に変換
      if (hours >= 24) {
        const nextDay = new Date(dateStr);
        nextDay.setDate(nextDay.getDate() + 1);
        finalDate = nextDay.toISOString().split('T')[0];
        adjustedHours = hours % 24;
      }
      
      const adjustedTime = `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      
      // SQLiteが受け付ける「YYYY-MM-DD HH:MM:SS」形式に変換
      const result = `${finalDate} ${adjustedTime}:00`;
      console.log('日時結合の結果:', result);
      
      return result;
    } catch (e) {
      console.error('日時結合エラー:', e, { dateStr, timeStr });
      return '';
    }
  };

  // データテーブル行クリック時にオーダーを選択
  const handleRowClick = (params: GridRowParams) => {
    const order = params.row as Order;
    
    console.log('編集前のオーダーデータ:', order);
    console.log('オーダーデータの全フィールド:', Object.keys(order));
    console.log('コース関連データ:', {
      course: order.course,
      course_name: order.course_name,
      course_duration: order.course_duration,
      course_id: order.course_id
    });
    console.log('数値フィールドデータ:', {
      discount: order.discount,
      service_time: order.service_time,
      castback: order.castback,
      revenue: order.revenue,
      // 他の可能性のあるフィールド名もチェック
      discount_amount: order.discount_amount,
      discount_value: order.discount_value,
      total_discount: order.total_discount,
      service_time_minutes: order.service_time_minutes,
      service_duration: order.service_duration,
      extra_time: order.extra_time,
      // プリミティブな値も確認
      typeof_discount: typeof order.discount,
      typeof_service_time: typeof order.service_time
    });
    
    // 予約日の処理
    let formattedPickupDate = '';
    
    // 複数のソースから予約日を取得を試行
    const pickupDateSources = [order.pickup_date, order.reservation_datetime, order.created_at];
    
    for (const dateSource of pickupDateSources) {
      if (dateSource && !formattedPickupDate) {
        try {
          if (typeof dateSource === 'string') {
            if (dateSource.includes('T')) {
              // ISO形式の場合
              formattedPickupDate = dateSource.split('T')[0];
              break;
            } else if (dateSource.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // すでにYYYY-MM-DD形式
              formattedPickupDate = dateSource;
              break;
            } else {
              // その他の日付形式を試行
              const date = new Date(dateSource);
              if (!isNaN(date.getTime())) {
                formattedPickupDate = date.toISOString().split('T')[0];
                break;
              }
            }
          }
        } catch (e) {
          console.error('予約日フォーマットエラー:', e, dateSource);
        }
      }
    }
    
    console.log('予約日の処理:', {
      pickup_date: order.pickup_date,
      reservation_datetime: order.reservation_datetime,
      created_at: order.created_at,
      result: formattedPickupDate
    });
    
    // 予約時間の処理（reservation_datetimeまたはreservation_timeから取得）
    let formattedReservationTime = '';
    if (order.reservation_datetime) {
      try {
        const date = new Date(order.reservation_datetime);
        if (!isNaN(date.getTime())) {
          formattedReservationTime = date.getHours().toString().padStart(2, '0') + ':' + 
                                     date.getMinutes().toString().padStart(2, '0');
        }
      } catch (e) {
        console.error('予約時間フォーマットエラー:', e);
      }
    } else if (order.reservation_time) {
      formattedReservationTime = order.reservation_time;
    } else if (order.delivery_date) {
      formattedReservationTime = formatTimeOnly(order.delivery_date);
    }
    
    // 入室時間の処理
    let formattedEntryTime = '';
    if (order.entry_time !== null && order.entry_time !== undefined) {
      try {
        if (typeof order.entry_time === 'string') {
          if (order.entry_time.trim() === '') {
            formattedEntryTime = '';
          } else if (order.entry_time.includes('T') || order.entry_time.includes(' ')) {
            // ISO形式またはデータベース日時形式
            const date = new Date(order.entry_time);
            if (!isNaN(date.getTime())) {
              formattedEntryTime = date.getHours().toString().padStart(2, '0') + ':' + 
                                   date.getMinutes().toString().padStart(2, '0');
            } else {
              formattedEntryTime = '';
            }
          } else if (order.entry_time.includes(':')) {
            // すでに時刻形式（HH:MM）
            formattedEntryTime = order.entry_time;
          } else {
            formattedEntryTime = '';
          }
        } else {
          formattedEntryTime = '';
        }
      } catch (e) {
        console.error('入室時間フォーマットエラー:', e);
        formattedEntryTime = '';
      }
    }
    
    // コース名の処理（DataGridと同じ方式）
    let courseName = '60分'; // デフォルト値
    
    // course_durationフィールドから時間を取得（DataGridと同様）
    if (order.course_duration && typeof order.course_duration === 'number') {
      courseName = `${order.course_duration}分`;
    } else if (order.course_duration && typeof order.course_duration === 'string') {
      // 文字列の場合は数値に変換を試行
      const duration = parseInt(order.course_duration);
      if (!isNaN(duration)) {
        courseName = `${duration}分`;
      }
    } else {
      // course_durationがない場合のフォールバック
      if (order.course && typeof order.course === 'number') {
        courseName = `${order.course}分`;
      } else if (order.course && typeof order.course === 'string' && order.course.includes('分')) {
        courseName = order.course;
      }
    }
    
    console.log('コース名の処理:', {
      course_duration: order.course_duration,
      course: order.course,
      course_name: order.course_name,
      result: courseName,
      availableCourses: availableCourses
    });
    
    // コース名が利用可能なコース一覧にない場合の処理
    if (!availableCourses.includes(courseName)) {
      console.log(`コース「${courseName}」が利用可能なコース一覧にありません。利用可能コース:`, availableCourses);
      
      // course_durationから直接コース名を作成
      if (order.course_duration) {
        const directCourseName = `${order.course_duration}分`;
        console.log(`course_durationから直接作成: ${directCourseName}`);
        
        // 作成したコース名が利用可能一覧にあるかチェック
        if (availableCourses.includes(directCourseName)) {
          courseName = directCourseName;
        } else {
          // 利用可能なコースにない場合は、一覧に追加してから使用
          console.log(`${directCourseName}を利用可能コース一覧に追加します`);
          courseName = directCourseName;
        }
      } else {
        courseName = availableCourses.length > 0 ? availableCourses[0] : '60分';
      }
    }
    
    // オプション詳細の処理
    let formattedOptionDetails = '[]';
    try {
      if (order.option_details) {
        if (typeof order.option_details === 'string') {
          // すでに文字列の場合
          if (order.option_details.includes('[object Object]')) {
            console.warn('無効なオプション詳細データを検出（handleRowClick）:', order.option_details);
            formattedOptionDetails = '[]';
          } else if (order.option_details.trim() === '') {
            formattedOptionDetails = '[]';
          } else {
            // 有効なJSON文字列かチェック
            try {
              JSON.parse(order.option_details);
              formattedOptionDetails = order.option_details;
            } catch (jsonError) {
              console.error('オプション詳細のJSON解析に失敗（handleRowClick）:', jsonError);
              formattedOptionDetails = '[]';
            }
          }
        } else if (Array.isArray(order.option_details)) {
          // 配列の場合はJSON文字列に変換
          console.log('オプション詳細を配列からJSON文字列に変換:', order.option_details);
          formattedOptionDetails = JSON.stringify(order.option_details);
        } else if (typeof order.option_details === 'object') {
          // オブジェクトの場合はJSON文字列に変換
          console.log('オプション詳細をオブジェクトからJSON文字列に変換:', order.option_details);
          formattedOptionDetails = JSON.stringify(order.option_details);
        } else {
          console.warn('予期しないオプション詳細の型（handleRowClick）:', typeof order.option_details, order.option_details);
          formattedOptionDetails = '[]';
        }
      } else if (order.castback || order.revenue) {
        // option_detailsがないがcastbackやrevenueがある場合、自動的にオプション詳細を作成
        const autoOptionDetails = [{
          name: order.options || 'サービス',
          castback: order.castback || 0,
          profit: order.revenue || 0
        }];
        formattedOptionDetails = JSON.stringify(autoOptionDetails);
        console.log('🔧 castback/revenueからオプション詳細を自動作成:', autoOptionDetails);
      }
    } catch (e) {
      console.error('オプション詳細の処理中にエラー（handleRowClick）:', e);
      formattedOptionDetails = '[]';
    }

    // 数値フィールドの安全な処理
    let discount = 0;
    let serviceTime = 0;
    
    // 割引額の処理（複数のフィールド名を試行）
    const discountFields = [
      order.discount,
      order.discount_amount,
      order.discount_value,
      order.total_discount
    ];
    
    for (const field of discountFields) {
      if (field !== null && field !== undefined && field !== 0) {
        const numValue = Number(field);
        if (!isNaN(numValue)) {
          discount = numValue;
          console.log('割引額を取得:', { field, processed: discount });
          break;
        }
      }
    }
    
    // サービスタイムの処理（複数のフィールド名を試行）
    const serviceTimeFields = [
      order.service_time,
      order.service_time_minutes,
      order.service_duration,
      order.extra_time
    ];
    
    for (const field of serviceTimeFields) {
      if (field !== null && field !== undefined && field !== 0) {
        const numValue = Number(field);
        if (!isNaN(numValue)) {
          serviceTime = numValue;
          console.log('サービスタイムを取得:', { field, processed: serviceTime });
          break;
        }
      }
    }
    
    console.log('数値フィールドの詳細処理:', {
      discount: { original: order.discount, type: typeof order.discount, processed: discount },
      service_time: { original: order.service_time, type: typeof order.service_time, processed: serviceTime }
    });
    
    // キャストの処理（cast_idsから取得）
    let castId = '';
    if (order.cast_ids && Array.isArray(order.cast_ids) && order.cast_ids.length > 0) {
      castId = String(order.cast_ids[0]);
    } else if (order.user_id) {
      // フォールバック: user_idがキャストIDの場合
      castId = String(order.user_id);
    }
    
    console.log('キャスト選択の処理:', {
      cast_ids: order.cast_ids,
      user_id: order.user_id,
      selected_cast_id: castId
    });
    
    // driver_idの処理
    let driverId = '';
    if (order.driver_id) {
      driverId = String(order.driver_id);
    }
    
    console.log('フィールド値の処理:', {
      cast_id: { original: order.cast_ids, processed: castId },
      driver_id: { original: order.driver_id, processed: driverId },
      discount: { original: order.discount, processed: discount },
      service_time: { original: order.service_time, processed: serviceTime }
    });

    // フォーマット済みオーダーを作成
    const formatted: Order = {
      ...order,
      pickup_date: formattedPickupDate,
      delivery_date: formattedReservationTime,
      reservation_time: formattedReservationTime,
      entry_time: formattedEntryTime,
      course: courseName,
      // その他のフィールドの安全な処理
      user_id: castId,
      driver_id: driverId,
      hotel_name: order.hotel_name || '',
      pickup_address: order.pickup_address || '',
      delivery_address: order.delivery_address || '',
      status: order.status || '予約',
      discount: discount,
      service_time: serviceTime,
      special_instructions: order.special_instructions || order.notes || '',
      option_details: formattedOptionDetails
    };
    
    console.log('編集用フォーマット済みオーダー:', formatted);
    console.log('データマッピング詳細:', {
      pickup_date: { original: order.pickup_date, formatted: formatted.pickup_date },
      reservation_time: { original: order.reservation_datetime || order.reservation_time, formatted: formatted.reservation_time },
      entry_time: { original: order.entry_time, formatted: formatted.entry_time },
      course: { original: order.course || order.course_name, formatted: formatted.course },
      cast_id: { original: order.cast_ids, formatted: formatted.user_id },
      option_details: { 
        original: order.option_details, 
        originalType: typeof order.option_details,
        formatted: formatted.option_details,
        formattedType: typeof formatted.option_details
      }
    });
    
    // キャストとドライバーのリスト確認
    console.log('選択可能なキャスト一覧:', casts.map(cast => ({ id: cast.id, name: formatCastDisplayName(cast) })));
    console.log('選択可能なドライバー一覧:', drivers.map(driver => ({ id: driver.id, name: driver.name })));
    console.log('選択可能なコース一覧:', availableCourses);
    
    // 現在の選択値がリストに存在するかチェック
    if (formatted.user_id) {
      const foundCast = casts.find(cast => String(cast.id) === String(formatted.user_id));
      console.log(`キャスト選択確認: ID=${formatted.user_id}, 見つかったキャスト:`, foundCast ? formatCastDisplayName(foundCast) : 'なし');
      
      // cast_idsとuser_idの対応関係をログ出力
      console.log(`キャストIDの詳細: cast_ids=${JSON.stringify(order.cast_ids)}, user_id=${order.user_id}, 選択されたID=${formatted.user_id}`);
    }
    
    if (formatted.driver_id) {
      const foundDriver = drivers.find(driver => String(driver.id) === String(formatted.driver_id));
      console.log(`ドライバー選択確認: ID=${formatted.driver_id}, 見つかったドライバー:`, foundDriver ? foundDriver.name : 'なし');
    }
    
    if (formatted.course) {
      const foundCourse = availableCourses.includes(formatted.course);
      console.log(`コース選択確認: コース=${formatted.course}, 利用可能なコースに存在:`, foundCourse ? 'はい' : 'いいえ');
      if (!foundCourse) {
        console.log('利用可能なコース一覧と一致しないため、最初のコースを使用します');
      }
    }
    
    setSelectedOrder(formatted);
  };

  // オーダーを更新する
  const handleUpdateOrder = async (order: Order) => {
    if (!order || !order.id) {
      showSnackbar('無効なオーダーです。', 'error');
      return;
    }
    try {
      // status が空のまま送るとサーバー側でステータスが消えてしまうため、
      // 空の場合は送信ペイロードから除外する（既存ステータスを維持）。
      const payload: any = { ...order };
      if (payload.status === '' || payload.status === null || payload.status === undefined) {
        delete payload.status;
      }
      const result = await orderService.updateOrder(order.id, payload);
      // orderService.updateOrder は失敗時に例外を投げず { error: true, ... } を返すため、
      // 戻り値を確認しないと失敗時にも「更新しました」と表示されてしまう。
      if (result && result.error) {
        console.error('Update failed:', result);
        showSnackbar(result.message || 'オーダーの更新に失敗しました。', 'error');
        return;
      }
      showSnackbar('オーダーを更新しました。', 'success');
      setSelectedOrder(null);
      // ダイアログを閉じるなどの処理
      if (selectedDate) {
        // fetchOrdersが定義されている場合のみ呼び出し
        if (typeof fetchOrders === 'function') {
          fetchOrders(selectedDate);
        } else {
          // fetchOrdersが未定義の場合は、setOrdersを直接使用
          setOrders(prev => prev.map(o => o.id === order.id ? order : o));
        }
      }
    } catch (error) {
      console.error('Update failed:', error);
      showSnackbar('オーダーの更新に失敗しました。', 'error');
    }
  };

  const handleDeleteOrder = async (orderId: number | string) => {
    try {
      await orderService.deleteOrder(orderId);
      showSnackbar('オーダーを削除しました。', 'success');
      if (selectedDate) {
        // fetchOrdersが定義されている場合のみ呼び出し
        if (typeof fetchOrders === 'function') {
          fetchOrders(selectedDate);
        } else {
          // fetchOrdersが未定義の場合は、setOrdersを直接使用
          setOrders(prev => prev.filter(o => o.id !== orderId));
        }
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showSnackbar('オーダーの削除に失敗しました。', 'error');
    }
  };

  // 顧客名を取得する
  const getCustomerName = (customerId: string | number): string => {
    const customer = customers.find(c => String(c.id) === String(customerId));
    return customer ? customer.name : '未割当';
  };

  // キャスト名を取得する
  const getCastName = (castId: string | number): string => {
    const cast = casts.find(c => String(c.id) === String(castId));
    return cast ? `${cast.first_name} ${cast.last_name}` : '未割当';
  };

  // 予約タイプを日本語に変換する
  const getOrderTypeInJapanese = (orderType: string): string => {
    switch (orderType) {
      case 'free':
        return 'フリー';
      case 'nomination':
        return '指名';
      case 'direct_nomination':
        return '本指名';
      default:
        return orderType || 'N/A';
    }
  };

  // キャスト名を適切に表示するヘルパー関数
  const formatCastDisplayName = (cast: any): string => {
    // first_name と last_name が両方存在する場合（優先）
    if (cast.first_name && cast.last_name && cast.last_name !== 'nan') {
      return `${cast.first_name} ${cast.last_name}`;
    }
    
    // first_name のみの場合
    if (cast.first_name && cast.first_name !== 'nan') {
      return cast.first_name;
    }
    
    // usernameからの抽出を試行
    if (cast.username) {
      const nameParts = cast.username.split(' ');
      return nameParts.length > 1 
        ? `${nameParts[0]} ${nameParts.slice(1).join(' ')}` 
        : nameParts[0];
    }
    
    // nameフィールドがある場合
    if (cast.name) {
      return cast.name;
    }
    
    // フォールバック
    return `ユーザー${cast.id}`;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // タイムゾーンの問題を回避するため、選択された日付の年月日をUTCとして扱う
      const correctedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      setSelectedDate(correctedDate);
    } else {
      setSelectedDate(null);
    }
  };

  // キャスト選択ハンドラ
  const handleCastChange = (castId: string) => {
    setSelectedCast(castId);
    // キャストプロフィールからオプションを取得（実際の実装ではAPIから取得する）
    // この例ではモックデータを使用
    const mockOptions = ['オプション1', 'オプション2', 'オプション3', 'オプション4'];
    setSelectedCastOptions(mockOptions);
    
    // 新規オーダーフォームでもキャストを選択した場合、オプションをリセット
    if (isCreateDialogOpen) {
      setNewOrder(prev => ({
        ...prev,
        user_id: castId,
        options: []
      }));
    }
  };

  // 新規オーダーダイアログを開く
  const openCreateOrderDialog = () => {
    setSelectedOrder(null);
    setNewOrder({
      customer_id: '',
      user_id: '',
      driver_id: '',
      status: '予約',
      description: '',
      order_type: 'free',
      pickup_address: '',
      delivery_address: '',
      pickup_date: format(new Date(), 'yyyy-MM-dd'),
      delivery_date: format(new Date(), 'HH:mm'),
      entry_time: format(new Date(), 'HH:mm'),
      course: '60分',
      options: [],
      discount: 0,
      special_instructions: ''
    });
    setIsCreateDialogOpen(true);
  };

  // 利用可能なドライバー一覧の取得
  const fetchAvailableDrivers = useCallback(async (date: string, time: string) => {
    try {
      // 実際にはAPIから時間内のシフトがあるドライバー一覧を取得する
      // 現在はモックデータを使用
      const mockDrivers = [
        { id: 1, name: 'ドライバーA' },
        { id: 2, name: 'ドライバーB' },
        { id: 3, name: 'ドライバーC' }
      ];
      setDrivers(mockDrivers);
    } catch (error) {
      console.error('ドライバー取得エラー:', error);
      setDrivers([]);
    }
  }, []);

  // 予約日時変更時のハンドラー
  const handleDateTimeChange = (field: 'pickup_date' | 'delivery_date', value: string) => {
    setNewOrder(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 予約日または予約時間が変更された場合、利用可能なドライバーを再取得
    if (field === 'pickup_date' || field === 'delivery_date') {
      fetchAvailableDrivers(
        newOrder.pickup_date,
        field === 'delivery_date' ? value : newOrder.delivery_date
      );
    }
  };

  // オプション変更ハンドラー
  const handleOptionChange = (option: string, checked: boolean) => {
    setNewOrder(prev => {
      const currentOptions = prev.options || [];
      const newOptions = checked
        ? [...currentOptions, option]
        : currentOptions.filter(opt => opt !== option);
      
      return {
        ...prev,
        options: newOptions
      };
    });
  };

  // ログインユーザーがキャストかどうかを判定する（顧客情報の表示制御に使用）
  const isCastUser = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const userData = JSON.parse(userStr);
      const role = String(userData.user?.role || userData.role || '').toUpperCase();
      return role === 'CAST';
    } catch {
      return false;
    }
  })();

  // データグリッドのカラム定義
  const columns = [
    {
      field: 'reservation_datetime',
      headerName: '予約時間',
      width: 120,
      renderCell: (params: any) => {
        try {
          if (!params.value) return '';
          const date = parseISO(params.value);
          return isValid(date) ? format(date, 'HH:mm') : '無効な日付';
        } catch (error) {
          console.error("Error formatting reservation_datetime:", error, "Value:", params.value);
          return 'エラー';
        }
      }
    },
    // 顧客(電話番号)列はキャストには表示しない
    ...(!isCastUser ? [{
      field: 'customer',
      headerName: '顧客',
      width: 150,
      renderCell: (params: any) => params.row.customer_phone || 'N/A'
    }] : []),
    {
      field: 'course',
      headerName: 'コース',
      width: 100,
      renderCell: (params: any) => `${params.row.course_duration || 'N/A'}分`
    },
    {
      field: 'cast',
      headerName: 'キャスト',
      width: 180,
      renderCell: (params: any) => {
        if (Array.isArray(params.row.cast_name) && params.row.cast_name.length > 0) {
          return params.row.cast_name.join(', ');
        }
        return params.row.cast_name || 'N/A';
      }
    },
    {
      field: 'hotel_name',
      headerName: 'ホテル',
      width: 180,
      valueGetter: (params: any) => params.row.hotel_name || 'N/A'
    },
    {
      field: 'order_type',
      headerName: '予約タイプ',
      width: 120,
      valueGetter: (params: any) => getOrderTypeInJapanese(params.row.order_type)
    },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 120,
      // キャスト画面と同じ判定: 入室時間が入力済み、またはステータスが完了なら「完了」と表示する。
      renderCell: (params: any) => {
        const status = params.row.status;
        const hasEntry = !!(params.row.entry_time || params.row.entry_time_value);
        if (status === '完了' || status === 'completed') return '完了';
        if (hasEntry && (!status || status === '予約')) return '完了';
        return status || '予約';
      }
    }
  ];



  // タイムスケジュール表示のためのヘルパー関数
  const getHourRange = () => {
    const hours = [];
    for (let i = timeRange.start; i <= timeRange.end; i++) {
      hours.push(i);
    }
    return hours;
  };

  const formatHour = (hour: number) => {
    const adjustedHour = hour % 24;
    const dayLabel = hour >= 24 ? '(翌日)' : '';
    return `${adjustedHour.toString().padStart(2, '0')}:00${dayLabel}`;
  };

  // コースの時間を分に変換
  const getCourseMinutes = (course: string | undefined): number => {
    if (!course) return 60; // デフォルト値
    const match = course.match(/(\d+)分/);
    return match ? parseInt(match[1]) : 60;
  };

  // 時間に分を加算して新しい時間文字列を返す（例: "18:30" + 60分 → "19:30"）
  const addMinutesToTime = (timeStr: string, minutes: number): string => {
    if (!timeStr) return '';
    const [hoursStr, minutesStr] = timeStr.split(':');
    const hours = parseInt(hoursStr);
    const mins = parseInt(minutesStr);
    
    // 合計分数を計算
    const totalMinutes = hours * 60 + mins + minutes;
    
    // 新しい時間と分を計算
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    
    // 時間文字列を返す
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  };

  // 時間を位置に変換（タイムライン表示用）
  const timeToPosition = (timeStr: string | undefined): number => {
    if (!timeStr) return 0;
    
    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    if (isNaN(hours) || isNaN(minutes)) return 0;

    // 深夜時間の処理（例：3:00 -> 27:00）。timeToPositionForShift と同じ扱いにして、
    // 深夜帯の予約・入室バーが左端にずれないようにする。
    if (hours < timeRange.start) {
      hours += 24;
    }
    
    // 開始時間からの相対位置（%）
    const pct = ((hours - timeRange.start) * 60 + minutes) / ((timeRange.end - timeRange.start) * 60) * 100;
    // 異常な時刻データでもバーが画面外に飛んで消えないよう 0〜100% にクランプする
    return Math.max(0, Math.min(100, pct));
  };

  // 時間幅を計算（タイムライン表示用）
  const calculateWidth = (startTime: string | undefined, course: string | undefined): number => {
    if (!startTime) return 0;
    
    const minutes = getCourseMinutes(course);
    return (minutes / ((timeRange.end - timeRange.start) * 60)) * 100;
  };

  // シフトデータを取得する関数
  const fetchShiftsData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('認証情報がありません。再度ログインしてください。', 'error');
        return;
      }

      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      console.log('シフトデータ取得開始:', { selectedDate, dateStr });
      
      // 他のAPIと同じく apiClient 経由で取得する（認証・プロキシ・baseURLが統一され確実）。
      // 生 axios 直叩きは本番(api.php プロキシ)環境で空になることがあるため使わない。
      const response = await api.get('/shifts', {
        params: {
          date: dateStr,
          per_page: 1000, // タイムラインは出勤キャストを全員表示するため、ページネーションで切れないよう上限を広げる
        },
      });

      console.log('シフトAPI生レスポンス:', response.data);
      console.log('シフトAPI status:', response.status);

      // APIレスポンスから正しくシフトデータを抽出
      const shiftsData = Array.isArray(response.data) ? response.data : 
                        (response.data.shifts ? response.data.shifts : []);
      
      console.log('抽出されたシフトデータ:', shiftsData);
      console.log('シフトデータ件数:', shiftsData.length);
      setShifts(shiftsData);
    } catch (error) {
      console.error('シフトデータの取得に失敗:', error);
      if (axios.isAxiosError(error)) {
        console.error('エラーレスポンス:', error.response?.data);
        console.error('エラーステータス:', error.response?.status);
      }
      showSnackbar('シフトデータの取得に失敗しました', 'error');
    }
  }, [selectedDate, showSnackbar]);

  // 初期データ読み込み時にシフトデータも取得
  useEffect(() => {
    fetchShiftsData();
  }, [fetchShiftsData, selectedDate]);

  // タイムラインビューをレンダリングする関数
  const renderTimelineView = () => {
    const hourRange = getHourRange();
    
    // シフトフィルタリングの高速化 - O(n²)からO(n)に改善
    const formattedSelectedDate = format(selectedDate, 'yyyy-MM-dd');
    
    // シフトのuser_idを配列に格納して高速検索できるようにする
    const castIdsWithShifts: string[] = [];
    const castShiftMap = new Map(); // キャストIDをキーに、シフト情報を格納するマップ
    
    console.log(`🔍 シフト処理開始: ${formattedSelectedDate}, 総シフト数: ${shifts.length}`);
    
    // まず全シフトをスキャンして該当日のシフトを持つキャストIDを抽出（O(n)の処理）
    shifts.forEach((shift) => {
      try {
        const shiftDate = shift.date ? format(new Date(shift.date), 'yyyy-MM-dd') : null;
        
        if (shiftDate === formattedSelectedDate && shift.user_id) {
          const castId = shift.user_id.toString();
          if (!castIdsWithShifts.includes(castId)) {
            castIdsWithShifts.push(castId);
          }
          
          // シフト情報をマップに格納（シフトに含まれるユーザー情報も保持し、
          // キャスト一覧(casts)に取得漏れがあっても名前を補完できるようにする）
          castShiftMap.set(castId, {
            start_time: shift.start_time || '10:00',
            end_time: shift.end_time || '22:00',
            break_start: shift.break_start,
            break_end: shift.break_end,
            shiftId: shift.id,
            user: (shift as any).user || null
          });
        }
      } catch (error) {
        console.error('シフト日付処理エラー:', error, shift);
      }
    });
    
    console.log(`該当日のシフトを持つキャスト数: ${castIdsWithShifts.length}`);
    console.log(`登録済みキャスト総数(casts): ${casts.length}`, casts.map(c => String(c.id)));
    console.log(`シフトありキャストID一覧:`, castIdsWithShifts);

    // 選択した日付にシフトがあるキャストを表示する。
    // まず casts(キャスト一覧)に居るものを採用。
    let castsWithShifts: any[] = casts.filter(cast => castIdsWithShifts.includes(cast.id.toString()));

    // casts に無いが該当日のシフトを持つ user を、シフトのユーザー情報から補完する。
    // ただしロールがキャスト(cast)のユーザーに限定し、ドライバー等が混ざらないようにする。
    const existingIds = new Set(castsWithShifts.map(c => String(c.id)));
    castIdsWithShifts.forEach(id => {
      if (existingIds.has(id)) return;
      const info = castShiftMap.get(id);
      const u = info?.user;
      const role = String(u?.role || '').toLowerCase();
      if (u && role === 'cast') {
        // casts と同じ形（id/first_name/last_name/username）に整形して追加
        castsWithShifts.push({
          id: u.id,
          first_name: u.first_name,
          last_name: u.last_name,
          username: u.username,
        });
        existingIds.add(id);
      } else if (u && !role) {
        // ロール情報が無い場合も、シフトがある以上は出勤者として表示する（名前はシフトのuserから）
        castsWithShifts.push({
          id: u.id,
          first_name: u.first_name,
          last_name: u.last_name,
          username: u.username,
        });
        existingIds.add(id);
      } else {
        console.warn(`⚠️ シフトありだが表示対象外(role=${role || '不明'}, user_id=${id})`);
      }
    });
    
    // シフト開始時間順にソート
    castsWithShifts = castsWithShifts.sort((a, b) => {
      const aShift = castShiftMap.get(a.id.toString());
      const bShift = castShiftMap.get(b.id.toString());
      
      // 両方シフトがあるので、開始時間順でソート
      if (aShift && bShift) {
        return aShift.start_time.localeCompare(bShift.start_time);
      }
      
      // フォールバック（本来はここには来ないはず）
      const aName = formatCastDisplayName(a);
      const bName = formatCastDisplayName(b);
      return aName.localeCompare(bName, 'ja');
    });
    
    console.log(`シフトありキャスト数: ${castsWithShifts.length}人`);
    
    // デバッグ: 表示されるキャストの一覧
    if (castsWithShifts.length <= 20) {
      castsWithShifts.forEach(cast => {
        const shiftInfo = castShiftMap.get(cast.id.toString());
        console.log(`表示キャスト: ${formatCastDisplayName(cast)} (ID: ${cast.id}) - ${shiftInfo.start_time}〜${shiftInfo.end_time}`);
      });
    }
    
    // パフォーマンス警告
    if (castsWithShifts.length > 50) {
      console.warn(`⚠️ 表示するキャスト数が多すぎます(${castsWithShifts.length}人)。パフォーマンスが低下する可能性があります。`);
    }

    // 時間を位置に変換（タイムライン表示用、シフト時間用）
    const timeToPositionForShift = (timeStr) => {
      if (!timeStr) return 0;
      
      const [hoursStr, minutesStr] = timeStr.split(':');
      let hours = parseInt(hoursStr);
      const minutes = parseInt(minutesStr);
      
      // 24時間表記で時間が範囲外の場合の対応
      if (isNaN(hours) || isNaN(minutes)) {
        console.error('不正な時間形式:', timeStr);
        return 0;
      }
      
      // 深夜時間の処理（例：3:00 -> 27:00）
      if (hours < timeRange.start) {
        hours += 24;
      }
      
      // 時間範囲外の場合の制限
      if (hours < timeRange.start) hours = timeRange.start;
      if (hours > timeRange.end) hours = timeRange.end;
      
      // 開始時間からの相対位置（%）
      return ((hours - timeRange.start) * 60 + minutes) / ((timeRange.end - timeRange.start) * 60) * 100;
    };

    // シフト幅を計算する関数の追加
    const calculateShiftWidth = (startTime, endTime) => {
      if (!startTime || !endTime) return 0;
      
      let [startHours, startMinutes] = startTime.split(':').map(Number);
      let [endHours, endMinutes] = endTime.split(':').map(Number);
      
      // 深夜時間の処理（例：3:00 -> 27:00）
      if (endHours < startHours) {
        endHours += 24;
      }
      
      // 時間範囲外の場合の制限
      if (startHours < timeRange.start) startHours = timeRange.start;
      if (endHours > timeRange.end) endHours = timeRange.end;
      
      // 合計分数を計算
      const startTotalMinutes = (startHours - timeRange.start) * 60 + startMinutes;
      const endTotalMinutes = (endHours - timeRange.start) * 60 + endMinutes;
      const durationMinutes = endTotalMinutes - startTotalMinutes;
      
      // 幅をパーセンテージで計算
      return (durationMinutes / ((timeRange.end - timeRange.start) * 60)) * 100;
    };

    return (
      <Box sx={{ mt: 2, overflowX: 'auto' }}>
        <Grid container>
          {/* ヘッダー行（時間軸） */}
          <Grid item xs={2}>
            <Box sx={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', borderBottom: '1px solid #ccc' }}>
              キャスト
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Box sx={{ display: 'flex', height: 60, borderBottom: '1px solid #ccc' }}>
              {hourRange.map(hour => (
                <Box key={hour} sx={{ 
                  flex: 1, 
                  textAlign: 'center', 
                  borderLeft: '1px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {formatHour(hour)}
                </Box>
              ))}
            </Box>
          </Grid>
          
          {/* キャスト行 */}
          {castsWithShifts.map(cast => {
            // このキャストの予約。担当キャストは cast_ids(配列)で管理されているため、
            // cast_ids に自分のIDが含まれるオーダーを紐付ける。
            // (従来は作成者を表す user_id で照合していたため、担当キャストにオーダーが出なかった)
            const castIdStr = String(cast.id);
            const castOrders = orders.filter(order => {
              const ids = Array.isArray(order.cast_ids) ? order.cast_ids.map((v: any) => String(v)) : [];
              if (ids.includes(castIdStr)) return true;
              // フォールバック: 旧データで cast_ids が無い場合は user_id で照合
              return String(order.user_id) === castIdStr;
            });
            
            // このキャストのシフト情報
            const shiftInfo = castShiftMap.get(cast.id.toString());
            
            return (
              <React.Fragment key={cast.id}>
                <Grid item xs={2}>
                  <Box sx={{ 
                    height: 60, 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: 1,
                    borderBottom: '1px solid #eee'
                  }}>
                    <Typography variant="body2">
                      {formatCastDisplayName(cast)}
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {shiftInfo.start_time && shiftInfo.start_time.substring(0, 5)}〜{shiftInfo.end_time && shiftInfo.end_time.substring(0, 5)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={10}>
                  <Box sx={{ 
                    position: 'relative', 
                    height: 60, 
                    borderBottom: '1px solid #eee'
                  }}>
                    {/* 時間区切り線 */}
                    {hourRange.map(hour => (
                      <Box key={hour} sx={{
                        position: 'absolute',
                        left: `${(hour - timeRange.start) / (timeRange.end - timeRange.start) * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        backgroundColor: '#ddd'
                      }} />
                    ))}
                    
                    {/* シフト時間帯の背景 */}
                    {shiftInfo && (
                      <Box sx={{
                        position: 'absolute',
                        left: `${timeToPositionForShift(shiftInfo.start_time)}%`,
                        width: `${calculateShiftWidth(shiftInfo.start_time, shiftInfo.end_time)}%`,
                        top: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(144, 202, 249, 0.3)', // 薄い青色
                        zIndex: 1
                      }} />
                    )}
                    
                    {/* 休憩時間帯の背景 */}
                    {shiftInfo && shiftInfo.break_start && shiftInfo.break_end && (
                      <Box sx={{
                        position: 'absolute',
                        left: `${timeToPositionForShift(shiftInfo.break_start)}%`,
                        width: `${calculateShiftWidth(shiftInfo.break_start, shiftInfo.break_end)}%`,
                        top: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 193, 7, 0.2)', // 薄い黄色
                        zIndex: 2
                      }} />
                    )}
                    
                    {/* 予約ブロック */}
                    {castOrders.map(order => {
                      // 入室時間（HH:MM）。バックエンドが strftime で返す entry_time_value を最優先に使う
                      // （ISOを new Date()で変換するとタイムゾーンで時刻がずれる可能性があるため）。
                      // 無ければ entry_time を HH:MM に正規化する。
                      const entryTimeStr = order.entry_time_value
                        ? formatTimeOnly(String(order.entry_time_value))
                        : (order.entry_time ? formatTimeOnly(String(order.entry_time)) : null);
                      // 予約時刻（HH:MM）。reservation_time(正規化済み) → reservation_datetime → delivery_date の順で取得。
                      // delivery_date はバックエンドの to_dict に無いため、これだけだと空になり予約バーが出なかった。
                      const reservationTimeStr =
                        formatTimeOnly(order.reservation_time) ||
                        formatTimeOnly(order.reservation_datetime) ||
                        formatTimeOnly(order.delivery_date);

                      // 予約も入室も時刻が無ければ描画しない
                      if (!reservationTimeStr && !entryTimeStr) return null;

                      // 塗りつぶす時間 = コース時間 + サービスタイム（追加時間）
                      // course_duration(数値・分) を優先し、無ければ course 文字列("60分")から抽出
                      const courseMinutes = (Number(order.course_duration) || 0) > 0
                        ? Number(order.course_duration)
                        : getCourseMinutes(order.course);
                      const serviceMinutes = Number(order.service_time) || 0;
                      const totalMinutes = courseMinutes + serviceMinutes;
                      // タイムライン全体に対する幅(%)
                      const widthPct = (totalMinutes / ((timeRange.end - timeRange.start) * 60)) * 100;

                      // 入室時間が入力されていれば「入室時間」を、無ければ「予約時間」を起点にする。
                      // 予約バーと入室バーで全く同じ計算ロジックを使い、起点の時刻と色だけを変える。
                      const isEntry = !!entryTimeStr;
                      const startStr = isEntry ? entryTimeStr : reservationTimeStr;
                      if (!startStr) return null;

                      // 起点・終点・位置（予約でも入室でも同一の計算）
                      const left = timeToPosition(startStr);
                      const exitStr = addMinutesToTime(startStr, totalMinutes);

                      return (
                        <Box
                          key={order.id}
                          sx={{
                            position: 'absolute',
                            left: `${left}%`,
                            width: `${widthPct}%`,
                            top: 5,
                            bottom: 5,
                            // 入室=緑、予約=薄い灰青
                            backgroundColor: isEntry ? 'rgba(102, 187, 106, 0.75)' : 'rgba(144, 164, 174, 0.35)',
                            border: isEntry ? '1px solid #43a047' : '1px dashed #90a4ae',
                            color: isEntry ? '#1b5e20' : '#455a64',
                            fontWeight: isEntry ? 'bold' : 'normal',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            zIndex: 5,
                            '&:hover': {
                              boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                              zIndex: 10,
                            },
                          }}
                          onClick={() => handleRowClick({ row: order } as GridRowParams)}
                        >
                          <div>{`${isEntry ? '入室' : '予約'}${startStr}`}</div>
                          <div>{`〜${exitStr}`}</div>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // CSVエクスポート処理
  const handleExportCSV = async () => {
    try {
      // 現在の検索条件を取得
      let queryParams = '';
      
      if (dateViewMode === 'daily') {
        queryParams = `?start_date=${format(selectedDate, 'yyyy-MM-dd')}`;
      } else if (dateViewMode === 'weekly') {
        queryParams = `?start_date=${format(startOfWeek(selectedDate), 'yyyy-MM-dd')}&end_date=${format(endOfWeek(selectedDate), 'yyyy-MM-dd')}`;
      }
      
      if (selectedCast) {
        queryParams += queryParams ? `&user_id=${selectedCast}` : `?user_id=${selectedCast}`;
      }
      
      // トークンを取得
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('認証情報がありません。再ログインしてください。', 'error');
        return;
      }
      
      console.log('CSVエクスポート - トークン:', token); // デバッグ用

      // ユーザー情報を確認
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('CSVエクスポート - ユーザー:', user);
      console.log('CSVエクスポート - ユーザーロール:', user.role);
      
      // CSVをダウンロード
      const response = await api.get(`/api/orders/export-csv${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // ダウンロードリンクを作成
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_export_${format(selectedDate, 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSnackbar('オーダーをCSVでエクスポートしました', 'success');
    } catch (err: any) {
      console.error('オーダーCSVエクスポートに失敗しました:', err);
      
      // エラーの詳細をログに記録
      if (err.response) {
        console.error('エラーステータス:', err.response.status);
        console.error('エラーデータ:', err.response.data);
      }
      
      showSnackbar('CSVエクスポートに失敗しました: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  // レスポンスの処理とエラーハンドリング
  const processOrderData = useCallback((orderData: any[]): Order[] => {
    if (!Array.isArray(orderData)) {
      console.error('オーダーデータが配列ではありません:', orderData);
      return [];
    }
    
    return orderData.map(order => {
      try {
        // 予約時間の処理
        let reservationTime = '';
        if (order.reservation_datetime) {
          try {
            const dt = parseISO(order.reservation_datetime);
            if (isValid(dt)) {
              reservationTime = format(dt, 'HH:mm');
            }
          } catch (e) {
            console.error('reservation_datetime のパースエラー:', e, order.reservation_datetime);
          }
        } else {
            reservationTime = order.reservation_time 
            || (order.pickup_date ? new Date(order.pickup_date).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : null)
            || (order.delivery_date ? new Date(order.delivery_date).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : null)
            || '';
        }
          
        // 入室時間の処理
        let entryTime = '';
        if (order.entry_time !== null && order.entry_time !== undefined) {
          try {
            if (typeof order.entry_time === 'string') {
              // 空文字列チェック
              if (order.entry_time.trim() === '') {
                entryTime = '';
              } else if (order.entry_time.includes('T') || order.entry_time.includes(' ')) {
                // ISO形式またはデータベース日時形式
                const entryDate = new Date(order.entry_time);
                if (!isNaN(entryDate.getTime())) {
                  entryTime = entryDate.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
                } else {
                  console.error('入室時間の日付変換に失敗:', order.entry_time);
                  entryTime = '';
                }
              } else if (order.entry_time.includes(':')) {
                // すでに時刻形式（HH:MM）
                entryTime = order.entry_time;
              } else {
                // その他の形式は空文字列とする
                console.warn('予期しない入室時間の形式:', order.entry_time);
                entryTime = '';
              }
            } else {
              // 文字列以外の場合
              console.warn('入室時間が文字列ではありません:', typeof order.entry_time, order.entry_time);
              entryTime = '';
            }
          } catch (e) {
            console.error('入室時間の解析エラー:', e, order.entry_time);
            entryTime = '';
          }
        }
          
        // option_detailsの処理
        let processedOptionDetails = '[]';
        try {
          if (order.option_details) {
            if (typeof order.option_details === 'string') {
              // 空文字列のチェック
              if (order.option_details.trim() === '') {
                processedOptionDetails = '[]';
              } else {
                // "[object Object]"のような無効な文字列をチェック
                if (order.option_details.includes('[object Object]')) {
                  console.warn('無効なオプション詳細データを検出しました:', order.option_details);
                  processedOptionDetails = '[]';
                } else {
                  // 有効なJSON形式かチェック
                  try {
                    const parsed = JSON.parse(order.option_details);
                    processedOptionDetails = order.option_details;
                  } catch (jsonError) {
                    console.error('オプション詳細の解析に失敗:', jsonError, 'データ:', order.option_details);
                    processedOptionDetails = '[]';
                  }
                }
              }
            } else if (Array.isArray(order.option_details)) {
              // 配列をJSON文字列に変換
              processedOptionDetails = JSON.stringify(order.option_details);
            } else if (typeof order.option_details === 'object') {
              // オブジェクトの場合はJSON文字列に変換
              try {
                processedOptionDetails = JSON.stringify(order.option_details);
              } catch (stringifyError) {
                console.error('オプション詳細のオブジェクト変換に失敗:', stringifyError);
                processedOptionDetails = '[]';
              }
            } else {
              console.warn('予期しないオプション詳細の型:', typeof order.option_details, order.option_details);
              processedOptionDetails = '[]';
            }
          } else {
            processedOptionDetails = '[]';
          }
        } catch (e) {
          console.error('オプション詳細の処理中にエラー:', e);
          processedOptionDetails = '[]';
        }
          
        // レスポンスの各フィールドを確認してログに出力
        console.log('オーダー時間関連データ確認:', {
          id: order.id,
          reservation_time: order.reservation_time,
          pickup_date: order.pickup_date,
          pickup_time: order.pickup_time,
          delivery_date: order.delivery_date,
          delivery_time: order.delivery_time,
          entry_time: order.entry_time,
          entry_time_type: typeof order.entry_time,
          parsed_entry_time: entryTime,
          parsed_time: reservationTime,
          option_details: processedOptionDetails
        });
          
        // デバッグ用のログを追加
        if (!entryTime && order.entry_time) {
          console.warn('入室時間の解析に失敗しました:', {
            original: order.entry_time,
            type: typeof order.entry_time
          });
        }
          
        return {
          ...order,
          reservation_time: reservationTime,
          // entry_time は元の値(ISO)のまま保持する。
          // HH:mm に変換すると toCastOrderView の退出時間計算(parseISO想定)が壊れるため。
          // 画面側は entry_time(ISO) / entry_time_value(HH:mm) を使い分ける。
          entry_time: order.entry_time,
          entry_time_value: order.entry_time_value || entryTime || undefined,
          option_details: processedOptionDetails,
          service_time: order.service_time || 0
        };
      } catch (error) {
        console.error('オーダー処理中にエラー:', error, order);
        return {
          ...order,
          error: 'データ処理エラー',
          option_details: '[]',
          service_time: 0
        };
      }
    });
  }, []);

  // システム設定を初期化時に取得
  const fetchSystemSettings = useCallback(async () => {
    console.log('🔧 fetchSystemSettings開始');
    try {
      // コース一覧を取得
      const courses = await getCourses();
      console.log('✅ getCourses()の結果:', courses);
      console.log('📊 コース一覧の型:', typeof courses);
      console.log('📋 コース一覧は配列か:', Array.isArray(courses));
      
      if (courses && courses.length > 0) {
        // 各コースの生データを確認
        console.log('最初のコースの生データ:', courses[0]);
        console.log('最初のコースのフィールド:', Object.keys(courses[0]));
        
        // CreateOrderと同様のフォーマットでコース情報を設定
        const formattedCourses = courses.map((course, index) => {
          console.log(`📝 コース${index + 1}の処理:`, {
            id: course.id,
            name: course.name,
            duration: course.duration,
            label_will_be: `${course.duration}分`,
            all_fields: Object.keys(course)
          });
          
          // nameではなくdurationを使ってラベルを作成
          const courseLabel = `${course.duration}分`;
          
          return {
            value: course.id,
            label: courseLabel,  // durationから作成したラベルを使用
            duration: course.duration,
            name: courseLabel    // nameもdurationから作成したラベルを使用
          };
        });
        
        // コース名とコースIDのマッピングを保存（name属性の確実な存在を保証）
        const coursesWithName: CourseItem[] = courses.map(course => ({
          ...course,
          name: `${course.duration}分` // durationから一貫したnameを生成
        }));
        setCoursesData(coursesWithName);
        console.log('💾 setCoursesData実行:', coursesWithName);
        
        // コース選択用に整形
        console.log('設定するコース情報:', formattedCourses);
        
        // 表示用のコース名リスト（例: "60分"）を設定
        const courseNames = formattedCourses.map(course => course.label);
        console.log('設定するコース名リスト:', courseNames);
        console.log('courseNamesの長さ:', courseNames.length);
        console.log('courseNamesの各要素:', courseNames.map((name, index) => `${index}: ${name}`));
        
        // 重複を除去してからセット
        const uniqueCourseNames = courseNames.filter((name, index) => courseNames.indexOf(name) === index);
        console.log('重複除去後のコース名リスト:', uniqueCourseNames);
        
        setAvailableCourses(uniqueCourseNames);
        
        // デバッグ: 各コースの詳細情報
        console.log('コース詳細情報:', courses.map(course => ({
          id: course.id,
          name: course.name,
          duration: course.duration,
          label: `${course.duration}分`
        })));
      } else {
        // デフォルトのコースを設定
        setAvailableCourses(['60分', '90分', '120分']);
      }
    } catch (error) {
      console.error('❌ システム設定の取得に失敗しました:', error);
      console.error('❌ エラー詳細:', error.message, error.stack);
      // エラー時はデフォルト値を使用
      const defaultCourses = ['60分', '90分', '120分'];
      console.log('🔄 デフォルトコースを設定:', defaultCourses);
      setAvailableCourses(defaultCourses);
    }
  }, []);

  // 顧客フィールドの表示用の関数
  const getCustomerDisplayValue = useCallback((customer) => {
    if (customer.phone) {
      return customer.phone;
    }
    return customer.name || `顧客ID: ${customer.id}`;
  }, []);

  // システム設定を初期化時に取得
  useEffect(() => {
    console.log('🚀 システム設定取得のuseEffect実行');
    fetchSystemSettings().catch(error => {
      console.error('❌ fetchSystemSettings実行エラー:', error);
    });
  }, [fetchSystemSettings]);

  // オーダー一覧を取得する
  const fetchOrders = useCallback(async (date: Date) => {
    console.log("fetchOrders called with date:", date);
    if (!allBusinessHours || allBusinessHours.length === 0) {
      console.log("Business hours not loaded yet, skipping fetch.");
      if(!settingsLoading){
        setError("営業時間設定が読み込まれていません。");
      }
      return;
    }
    
    setIsLoading(true);
    setError(null);
  
    try {
      // 営業日の開始・終了時刻を計算
      const dayOfWeek = date.getDay(); // Sunday: 0, Monday: 1, ...
      const businessHoursForDay = allBusinessHours.find(bh => bh.day_of_week === dayOfWeek);

      let start, end;

      if (businessHoursForDay && businessHoursForDay.is_business_day) {
        const [startHours, startMinutes] = businessHoursForDay.start_time.split(':').map(Number);
        const [endHours, endMinutes] = businessHoursForDay.end_time.split(':').map(Number);
        
        start = new Date(date);
        start.setHours(startHours, startMinutes, 0, 0);

        end = new Date(date);
        end.setHours(endHours, endMinutes, 0, 0);
        
        // 終了時刻が開始時刻より早い場合（日付をまたぐ場合）は、終了日を翌日にする
        if (end <= start) {
          end = addDays(end, 1);
        }

      } else {
        // 営業日でない場合は、その日の0時から23時59分までを範囲とする
        start = startOfDay(date);
        end = endOfDay(date);
      }
      
      console.log(`Fetching orders for business date: ${date.toISOString()}`);
      console.log(`Calculated start: ${start.toISOString()}, end: ${end.toISOString()}`);

      const formattedStartDate = formatDateTimeForServer(start);
      const formattedEndDate = formatDateTimeForServer(end);
      
      console.log('Fetching orders with params:', { 
        start_date: formattedStartDate, 
        end_date: formattedEndDate,
        cast_id: selectedCast || undefined 
      });

      const data = await orderService.getOrders({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        cast_id: selectedCast || undefined,
      });
      
      console.log('Fetched orders data:', data);

      if (data && Array.isArray(data.orders)) {
        // 生データの詳細を確認
        console.log('Orders 生データ (最初の1件):', data.orders[0]);
        if (data.orders[0]) {
          console.log('生データのフィールド一覧:', Object.keys(data.orders[0]));
          console.log('生データのコース関連:', {
            course: data.orders[0].course,
            course_name: data.orders[0].course_name,
            course_duration: data.orders[0].course_duration,
            course_id: data.orders[0].course_id
          });
          console.log('生データの数値フィールド:', {
            discount: data.orders[0].discount,
            service_time: data.orders[0].service_time,
            castback: data.orders[0].castback,
            revenue: data.orders[0].revenue
          });
        }
        
        // processOrderDataを通してデータを処理
        const processedOrders = processOrderData(data.orders);
        setOrders(processedOrders);
        console.log('Orders state updated with processed data:', processedOrders);
      } else if (Array.isArray(data)) {
        // Handle cases where the API returns a plain array
        const processedOrders = processOrderData(data);
        setOrders(processedOrders);
        console.log('Orders state updated with processed plain array:', processedOrders);
      } else {
        console.warn('Unexpected data structure for orders:', data);
        setOrders([]);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      const errorMessage = err instanceof Error ? err.message : 'オーダーの取得に失敗しました。';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [allBusinessHours, selectedCast, settingsLoading, showSnackbar]);

  useEffect(() => {
    if (selectedDate && !settingsLoading && allBusinessHours && allBusinessHours.length > 0) {
      fetchOrders(selectedDate);
    }
  }, [selectedDate, dateViewMode, selectedCast, settingsLoading, allBusinessHours, fetchOrders]);

  useEffect(() => {
    const initialFetch = async () => {
      // checkUserPermission のみ実行
      if (!checkUserPermission()) return;

      try {
        // 並行して実行。CAST/DRIVER は顧客一覧・ユーザー一覧の取得権限が無く 403 になるため、
        // Promise.allSettled で個別に成否を扱い、一部が失敗しても他の処理を止めない。
        const [customersSettled, usersSettled, coursesSettled] = await Promise.allSettled([
          getCustomers(),
          userService.getUsers(1, 0), // 全件取得（フィルターなし）
          getCourses()
        ]);

        const customersData = customersSettled.status === 'fulfilled' ? customersSettled.value : null;
        const usersData = usersSettled.status === 'fulfilled' ? usersSettled.value : null;
        const coursesResult = coursesSettled.status === 'fulfilled' ? coursesSettled.value : null;

        if (customersSettled.status === 'rejected') {
          console.warn('顧客一覧の取得をスキップ（権限不足の可能性）:', customersSettled.reason);
        }
        if (usersSettled.status === 'rejected') {
          console.warn('ユーザー一覧の取得をスキップ（権限不足の可能性）:', usersSettled.reason);
        }
        if (coursesSettled.status === 'rejected') {
          console.warn('コース一覧の取得に失敗:', coursesSettled.reason);
        }

        console.log('Fetched customers:', customersData);
        if (customersData && Array.isArray(customersData.customers)) {
          setCustomers(customersData.customers);
        } else if (Array.isArray(customersData)) {
           setCustomers(customersData);
        }

        console.log('Fetched users:', usersData);
        if (usersData && Array.isArray(usersData.users)) {
          const fetchedUsers = usersData.users;
          const castUsers = fetchedUsers.filter(u => u.role?.toLowerCase() === 'cast');
          const driverUsers = fetchedUsers.filter(u => u.role?.toLowerCase() === 'driver');
          
          console.log(`🎭 取得したキャスト数: ${castUsers.length}人`);
          console.log(`🚗 取得したドライバー数: ${driverUsers.length}人`);
          console.log('キャストデータ詳細:', castUsers.map(cast => ({ id: cast.id, first_name: cast.first_name, last_name: cast.last_name })));
          console.log('ドライバーデータ詳細:', driverUsers.map(driver => ({ id: driver.id, name: driver.first_name + ' ' + driver.last_name })));
          
          // バックエンドから正しいデータが来るのでそのまま使用
          const processedCasts = castUsers.map(user => ({
            id: user.id, 
            first_name: user.first_name, 
            last_name: user.last_name,
            username: user.username  // デバッグ用に保持
          }));
          
          const processedDrivers = driverUsers.map(user => ({
            id: user.id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `ドライバー${user.id}`,
            first_name: user.first_name,
            last_name: user.last_name
          }));
          
          setCasts(processedCasts);
          setDrivers(processedDrivers);
          
          console.log('処理後のキャスト:', processedCasts);
          console.log('処理後のドライバー:', processedDrivers);
        } else if (Array.isArray(usersData)) {
            const castUsers = usersData.filter(u => u.role?.toLowerCase() === 'cast');
            const driverUsers = usersData.filter(u => u.role?.toLowerCase() === 'driver');
            
            console.log(`🎭 取得したキャスト数(直接配列): ${castUsers.length}人`);
            console.log(`🚗 取得したドライバー数(直接配列): ${driverUsers.length}人`);
            console.log('キャストデータ詳細(直接配列):', castUsers.map(cast => ({ id: cast.id, first_name: cast.first_name, last_name: cast.last_name })));
            console.log('ドライバーデータ詳細(直接配列):', driverUsers.map(driver => ({ id: driver.id, name: driver.first_name + ' ' + driver.last_name })));
            
            // バックエンドから正しいデータが来るのでそのまま使用
            const processedCasts = castUsers.map(user => ({
              id: user.id, 
              first_name: user.first_name, 
              last_name: user.last_name,
              username: user.username  // デバッグ用に保持
            }));
            
            const processedDrivers = driverUsers.map(user => ({
              id: user.id,
              name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `ドライバー${user.id}`,
              first_name: user.first_name,
              last_name: user.last_name
            }));
            
            setCasts(processedCasts);
            setDrivers(processedDrivers);
            
            console.log('処理後のキャスト(直接配列):', processedCasts);
            console.log('処理後のドライバー(直接配列):', processedDrivers);
        }
        
        console.log('Fetched courses:', coursesResult);
        // coursesResultが直接配列であると仮定して処理
        if (Array.isArray(coursesResult)) {
          // コース名(c.name)はマスタ上「新しいコース」等になっており分かりにくいため、
          // 表示・値ともに duration ベース（例: 60分）に統一する。
          const coursesWithDurationName = coursesResult.map((c: any) => ({
            ...c,
            name: `${c.duration}分`,
          }));
          setCoursesData(coursesWithDurationName);
          const durationNames = coursesWithDurationName
            .map((c: any) => c.name)
            .filter((name: string, idx: number, arr: string[]) => arr.indexOf(name) === idx);
          setAvailableCourses(durationNames);
        }

      } catch (err) {
        console.error('Initial data fetch failed:', err);
        const errorMessage = err instanceof Error ? err.message : '初期データの取得に失敗しました。';
        setError(errorMessage);
        showSnackbar(errorMessage, 'error');
      }
    };
  
    initialFetch();
  }, [checkUserPermission, showSnackbar]);

  // 行のクラス名を設定する関数（ステータスに基づいて色を変える）
  const getRowClassName = (params: GridRowParams) => {
    const status = params.row.status?.toLowerCase();
    const hasEntry = !!(params.row.entry_time || params.row.entry_time_value);
    
    if (status === 'completed' || status === '完了' || (hasEntry && (!status || status === '予約'))) {
      return 'completed-row';
    } else if (status === 'cancelled' || status === 'キャンセル') {
      return 'cancelled-row';
    } else if (status === 'changed' || status === 'チェンジ') {
      return 'changed-row';
    }
    
    return '';
  };

  // オーダーのステータス表示（キャスト画面と同じ判定。キャンセル/チェンジは完了にしない）
  const getStatusLabel = (order: any): string => {
    const status = order?.status;
    if (status === 'キャンセル' || status === 'cancelled') return 'キャンセル';
    if (status === 'チェンジ' || status === 'changed') return 'チェンジ';
    if (status === '完了' || status === 'completed') return '完了';
    const hasEntry = !!(order?.entry_time || order?.entry_time_value);
    if (hasEntry) return '完了';
    return status || '予約';
  };

  // 退出時刻のミリ秒（ソート用）。入室時刻 + コース + サービスタイム。無ければ末尾へ送る。
  const getExitSortKey = (order: any): number => {
    let base: Date | null = null;
    if (order?.entry_time) {
      try { base = parseISO(order.entry_time); } catch { base = null; }
    }
    if (!base && order?.reservation_datetime) {
      try { base = parseISO(order.reservation_datetime); } catch { base = null; }
    }
    if (!base || isNaN(base.getTime())) return Number.MAX_SAFE_INTEGER;
    const courseMinutes = Number(order?.course_duration) || 0;
    const serviceMinutes = Number(order?.service_time) || 0;
    if (courseMinutes <= 0) return Number.MAX_SAFE_INTEGER;
    return base.getTime() + (courseMinutes + serviceMinutes) * 60 * 1000;
  };

  // 退出時刻ラベル(HH:mm)。入室時刻(なければ予約時刻)+コース+サービスタイムで算出。
  // course_duration が無ければ null。castOrderUtils の計算に依存せず確実に出す。
  const getExitLabel = (order: any): string | null => {
    const key = getExitSortKey(order);
    if (key === Number.MAX_SAFE_INTEGER) return null;
    const d = new Date(key);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // ステータス並べ替え用の順位（予約→完了→チェンジ→キャンセル）
  const getStatusRank = (order: any): number => {
    const label = getStatusLabel(order);
    switch (label) {
      case '予約': return 0;
      case '完了': return 1;
      case 'チェンジ': return 2;
      case 'キャンセル': return 3;
      default: return 4;
    }
  };

  // カード一覧用に、選択中の基準でソートしたオーダー配列を返す
  const getSortedCardOrders = (): any[] => {
    const list = [...orders];
    const dir = cardSortDir === 'asc' ? 1 : -1;
    list.sort((a: any, b: any) => {
      if (cardSortKey === 'name') {
        const an = getCustomerName(a.customer_id || '') || '';
        const bn = getCustomerName(b.customer_id || '') || '';
        return an.localeCompare(bn, 'ja') * dir;
      }
      if (cardSortKey === 'exit') {
        return (getExitSortKey(a) - getExitSortKey(b)) * dir;
      }
      if (cardSortKey === 'status') {
        const r = getStatusRank(a) - getStatusRank(b);
        // 同ステータス内は予約時間の早い順で安定させる
        if (r !== 0) return r * dir;
        return (toCastOrderView(a).reservationSortKey - toCastOrderView(b).reservationSortKey);
      }
      // reservation（予約時間）
      const av = toCastOrderView(a).reservationSortKey;
      const bv = toCastOrderView(b).reservationSortKey;
      return (av - bv) * dir;
    });
    return list;
  };

  // ステータスに応じたカードの枠色・背景
  const getCardSx = (order: any) => {
    const label = getStatusLabel(order);
    if (label === '完了') return { borderColor: 'success.main', bgcolor: 'rgba(0,128,0,0.06)' };
    if (label === 'キャンセル') return { borderColor: 'error.main', bgcolor: 'rgba(255,0,0,0.06)' };
    if (label === 'チェンジ') return { borderColor: 'warning.main', bgcolor: 'rgba(255,165,0,0.08)' };
    return undefined;
  };

  // カード形式のオーダー一覧をレンダリング（PCはグリッド、スマホは1列）
  const renderOrderCards = () => {
    const sorted = getSortedCardOrders();
    return (
      <Box>
        {/* ソートバー */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={cardSortKey}
            onChange={(_e, val) => val && setCardSortKey(val)}
          >
            <ToggleButton value="reservation">予約時間</ToggleButton>
            <ToggleButton value="name">名前</ToggleButton>
            <ToggleButton value="exit">退出時間</ToggleButton>
            <ToggleButton value="status">予約・完了</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={cardSortDir}
            onChange={(_e, val) => val && setCardSortDir(val)}
          >
            <ToggleButton value="asc">昇順</ToggleButton>
            <ToggleButton value="desc">降順</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Grid container spacing={2}>
          {sorted.map((order: any) => {
            const view = toCastOrderView(order);
            const statusLabel = getStatusLabel(order);
            // 顧客名を優先し、取得できない場合は電話番号を表示する
            const resolvedName = getCustomerName(order.customer_id || '');
            const customerDisplay =
              resolvedName && resolvedName !== '未割当'
                ? resolvedName
                : (order.customer_phone || '未割当');
            // 担当ドライバー（入室/退出）の表示名
            const entryDriverName = order.entry_driver?.name || '未割当';
            const exitDriverName = order.exit_driver?.name || '未割当';
            return (
              <Grid item xs={12} sm={6} md={4} key={order.id}>
                <Card variant="outlined" sx={{ height: '100%', ...getCardSx(order) }}>
                  <CardActionArea onClick={() => handleRowClick({ row: order } as GridRowParams)} sx={{ height: '100%' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{view.reservationLabel}</Typography>
                        </Stack>
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={
                            statusLabel === '完了' ? 'success'
                            : statusLabel === 'キャンセル' ? 'error'
                            : statusLabel === 'チェンジ' ? 'warning'
                            : 'default'
                          }
                        />
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <SpaIcon fontSize="small" color="action" />
                          <Typography variant="body1">{view.courseLabel}</Typography>
                        </Stack>
                        <Chip label={view.orderTypeLabel} size="small" color="primary" variant="outlined" />
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          顧客: {customerDisplay} / キャスト: {view.castNameLabel}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <HotelIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>{view.hotelLabel}</Typography>
                      </Stack>

                      {/* 担当ドライバー（入室・退出） */}
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <DirectionsCarIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          入室: {entryDriverName} / 退出: {exitDriverName}
                        </Typography>
                      </Stack>

                      {(() => {
                        const entryLabel = view.entryTimeLabel;
                        const exitLabel = getExitLabel(order);
                        if (!entryLabel && !exitLabel) return null;
                        return (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                              {entryLabel && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <LoginIcon fontSize="small" color="success" />
                                  <Typography variant="body2">入室 {entryLabel}</Typography>
                                </Stack>
                              )}
                              {exitLabel && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <LogoutIcon fontSize="small" color="action" />
                                  <Typography variant="body2">退出 {exitLabel}</Typography>
                                </Stack>
                              )}
                            </Stack>
                          </>
                        );
                      })()}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // メインコンテンツをレンダリングする関数
  const renderContent = () => {
    if (settingsLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Container maxWidth="lg">
          <Box p={3}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
            >
              再読み込み
            </Button>
          </Box>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PageHeader title="オーダー管理" />
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="div" style={{ fontWeight: 'bold' }}>
              オーダー管理
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
            >
              CSVエクスポート
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={navigateToCreateOrder}
            >
              オーダー作成
            </Button>
          </Stack>
        </Box>
        
        {/* フィルターツールバー */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {/* 日付フィルター */}
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>表示期間</InputLabel>
                <Select
                  value={dateViewMode}
                  label="表示期間"
                  onChange={(e) => setDateViewMode(e.target.value as 'daily' | 'weekly')}
                >
                  <MenuItem value="daily">日</MenuItem>
                  <MenuItem value="weekly">週</MenuItem>
                </Select>
              </FormControl>
              
              {/* 日付選択 */}
              <BusinessDatePicker
                label="表示日"
                value={selectedDate}
                onChange={handleDateChange}
              />
              
              {/* キャスト選択 */}
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>キャスト</InputLabel>
                <Select
                  value={selectedCast}
                  label="キャスト"
                  onChange={(e) => setSelectedCast(String(e.target.value))}
                >
                  <MenuItem value="">すべて</MenuItem>
                  {casts.map((cast) => (
                    <MenuItem key={cast.id} value={String(cast.id)}>
                      {formatCastDisplayName(cast)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 表示モード切替 */}
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>表示モード</InputLabel>
                <Select
                  value={viewMode}
                  label="表示モード"
                  onChange={(e) => setViewMode(e.target.value as 'list' | 'timeline')}
                >
                  <MenuItem value="list">リスト</MenuItem>
                  <MenuItem value="timeline">タイムライン</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Toolbar>
        </Paper>
        
        {/* オーダーリスト */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            オーダー一覧
          </Typography>
          
          {orders.length > 0 ? (
            viewMode === 'list' ? (
              renderOrderCards()
            ) : (
              renderTimelineView()
            )
          ) : (
            <Alert severity="info">
              表示期間内のオーダーはありません。新しいオーダーを作成するか、別の期間を選択してください。
            </Alert>
          )}
        </Paper>
      </Container>
    );
  };

  // ダイアログ等の補助コンポーネント
  const renderDialogs = () => (
    <>
      {/* 新規オーダー作成ダイアログ */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>新規オーダー</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>予約タイプ</InputLabel>
                <Select
                  value={newOrder.order_type}
                  label="予約タイプ"
                  onChange={(e) => setNewOrder({...newOrder, order_type: e.target.value as 'free' | 'nomination' | 'direct_nomination'})}
                >
                  <MenuItem value="free">フリー</MenuItem>
                  <MenuItem value="nomination">指名</MenuItem>
                  <MenuItem value="direct_nomination">本指名</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>コース</InputLabel>
                <Select
                  value={newOrder.course}
                  label="コース"
                  onChange={(e) => setNewOrder({...newOrder, course: e.target.value})}
                >
                  {availableCourses.map(course => (
                    <MenuItem key={course} value={course}>{course}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>顧客</InputLabel>
                <Select
                  value={newOrder.customer_id}
                  label="顧客"
                  onChange={(e) => setNewOrder({...newOrder, customer_id: e.target.value})}
                >
                  {Array.isArray(customers) ? customers.map(customer => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.phone || customer.name}
                    </MenuItem>
                  )) : (
                    <MenuItem value="">顧客データを読み込み中...</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>キャスト</InputLabel>
                <Select
                  value={newOrder.user_id}
                  label="キャスト"
                  onChange={(e) => {
                    const castId = e.target.value as string;
                    setNewOrder({...newOrder, user_id: castId});
                    handleCastChange(castId);
                  }}
                >
                  {casts.map(cast => (
                    <MenuItem key={cast.id} value={cast.id}>
                      {formatCastDisplayName(cast)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* キャスト選択時にのみオプション表示 */}
            {newOrder.user_id && selectedCastOptions.length > 0 && (
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>オプション</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedCastOptions.map(option => (
                    <FormControl key={option} sx={{ m: 1 }}>
                      <label>
                        <input 
                          type="checkbox"
                          checked={newOrder.options?.includes(option) || false}
                          onChange={(e) => handleOptionChange(option, e.target.checked)}
                        />
                        {option}
                      </label>
                    </FormControl>
                  ))}
                </Box>
            </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="予約日"
                type="date"
                value={newOrder.pickup_date}
                onChange={(e) => handleDateTimeChange('pickup_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="予約時間"
                type="time"
                value={newOrder.delivery_date}
                onChange={(e) => handleDateTimeChange('delivery_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="入室時間"
                type="time"
                value={newOrder.entry_time || ''}
                onChange={(e) => setNewOrder({...newOrder, entry_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>担当ドライバー</InputLabel>
                <Select
                  value={newOrder.driver_id || ''}
                  label="担当ドライバー"
                  onChange={(e) => setNewOrder({...newOrder, driver_id: e.target.value})}
                >
                  <MenuItem value="">選択なし</MenuItem>
                  {drivers.map(driver => (
                    <MenuItem key={driver.id} value={driver.id}>{driver.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="送迎"
                value={newOrder.pickup_address}
                onChange={(e) => setNewOrder({...newOrder, pickup_address: e.target.value})}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ホテル"
                value={newOrder.delivery_address}
                onChange={(e) => setNewOrder({...newOrder, delivery_address: e.target.value})}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={newOrder.status}
                  label="ステータス"
                  onChange={(e) => setNewOrder({...newOrder, status: e.target.value})}
                >
                  <MenuItem value="予約">予約</MenuItem>
                  <MenuItem value="完了">完了</MenuItem>
                  <MenuItem value="チェンジ">チェンジ</MenuItem>
                  <MenuItem value="キャンセル">キャンセル</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="割引額"
                value={newOrder.discount || 0}
                onChange={(e) => setNewOrder({...newOrder, discount: parseInt(e.target.value) || 0})}
                InputProps={{
                  startAdornment: <span>¥</span>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="特記事項"
                value={newOrder.special_instructions || ''}
                onChange={(e) => setNewOrder({...newOrder, special_instructions: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>キャンセル</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCreateOrder}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      {selectedOrder && (
        <Dialog open={!!selectedOrder} onClose={() => setSelectedOrder(null)} maxWidth="md" fullWidth>
          <DialogTitle>オーダー編集</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>予約タイプ</InputLabel>
                  <Select
                    value={selectedOrder.order_type || 'free'}
                    label="予約タイプ"
                    onChange={(e) => setSelectedOrder({
                      ...selectedOrder, 
                      order_type: e.target.value as 'free' | 'nomination' | 'direct_nomination'
                    })}
                  >
                    <MenuItem value="free">フリー</MenuItem>
                    <MenuItem value="nomination">指名</MenuItem>
                    <MenuItem value="direct_nomination">本指名</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>コース</InputLabel>
                  <Select
                    value={selectedOrder.course || '60分'}
                    label="コース"
                    onChange={(e) => setSelectedOrder({...selectedOrder, course: e.target.value})}
                  >
                    {availableCourses.map(course => (
                      <MenuItem key={course} value={course}>{course}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {!isCastUser && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="顧客"
                    value={selectedOrder.customer_phone || getCustomerName(selectedOrder.customer_id || '')}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>キャスト</InputLabel>
                  <Select
                    value={selectedOrder.user_id ? String(selectedOrder.user_id) : ''}
                    label="キャスト"
                    onChange={(e) => {
                      const castId = e.target.value;
                      setSelectedOrder({
                        ...selectedOrder, 
                        user_id: castId === '' ? undefined : castId
                      });
                      handleCastChange(castId);
                    }}
                  >
                    <MenuItem value="">選択なし</MenuItem>
                    {casts.map((cast) => (
                      <MenuItem key={cast.id} value={String(cast.id)}>
                        {formatCastDisplayName(cast)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="予約日"
                  type="date"
                  value={selectedOrder.pickup_date || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, pickup_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="予約時間"
                  type="time"
                  value={selectedOrder.reservation_time || selectedOrder.delivery_date || ''}
                  onChange={(e) => {
                    setSelectedOrder({...selectedOrder, delivery_date: e.target.value, reservation_time: e.target.value});
                    fetchAvailableDrivers(selectedOrder.pickup_date || '', e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="入室時間"
                  type="time"
                  value={selectedOrder.entry_time || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, entry_time: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>担当ドライバー</InputLabel>
                  <Select
                    value={selectedOrder.driver_id ? String(selectedOrder.driver_id) : ''}
                    label="担当ドライバー"
                    onChange={(e) => setSelectedOrder({
                      ...selectedOrder, 
                      driver_id: e.target.value === '' ? undefined : e.target.value
                    })}
                  >
                    <MenuItem value="">選択なし</MenuItem>
                    {drivers.map(driver => (
                      <MenuItem key={driver.id} value={String(driver.id)}>
                        {driver.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ホテル名"
                  value={selectedOrder.hotel_name || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, hotel_name: e.target.value})}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="送迎"
                  value={selectedOrder.pickup_address || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, pickup_address: e.target.value})}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ホテル住所"
                  value={selectedOrder.delivery_address || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, delivery_address: e.target.value})}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="部屋番号"
                  placeholder="部屋番号が決まったら入力（保存するとLINE通知されます）"
                  value={selectedOrder.room_number || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, room_number: e.target.value})}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>ステータス</InputLabel>
                  <Select
                    value={selectedOrder.status || '予約'}
                    label="ステータス"
                    onChange={(e) => setSelectedOrder({...selectedOrder, status: e.target.value})}
                  >
                    <MenuItem value="予約">予約</MenuItem>
                    <MenuItem value="完了">完了</MenuItem>
                    <MenuItem value="チェンジ">チェンジ</MenuItem>
                    <MenuItem value="キャンセル">キャンセル</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="キャストバック"
                  value={selectedOrder.castback ?? 0}
                  onChange={(e) => setSelectedOrder({...selectedOrder, castback: parseInt(e.target.value) || 0})}
                  InputProps={{
                    startAdornment: <span>¥</span>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="総額"
                  value={selectedOrder.grand_total ?? 0}
                  onChange={(e) => setSelectedOrder({...selectedOrder, grand_total: parseInt(e.target.value) || 0})}
                  InputProps={{
                    startAdornment: <span>¥</span>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="割引額"
                  value={selectedOrder.discount || 0}
                  onChange={(e) => setSelectedOrder({...selectedOrder, discount: parseInt(e.target.value) || 0})}
                  InputProps={{
                    startAdornment: <span>¥</span>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="サービスタイム（分）"
                  value={selectedOrder.service_time || 0}
                  onChange={(e) => setSelectedOrder({...selectedOrder, service_time: parseInt(e.target.value) || 0})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>オプション詳細</Typography>
                {selectedOrder.option_details ? (
                  (() => {
                    try {
                      // 空文字列や不正なJSON形式の場合は初期値を設定
                      let detailsStr = selectedOrder.option_details || '[]';
                      
                      // 文字列でない場合は空の配列に変換
                      if (typeof detailsStr !== 'string') {
                        console.warn('オプション詳細が文字列ではありません（編集ダイアログ）:', typeof detailsStr, detailsStr);
                        detailsStr = '[]';
                      } else {
                        // "[object Object]"のような無効な文字列をチェック
                        if (detailsStr.includes('[object Object]')) {
                          console.warn('無効なオプション詳細データを検出しました（編集ダイアログ）:', detailsStr);
                          detailsStr = '[]';
                        }
                        
                        if (detailsStr.trim() === '') {
                          detailsStr = '[]';
                        }
                      }
                      
                      const details = JSON.parse(detailsStr);
                      
                      // 配列でない場合は空配列にする
                      if (!Array.isArray(details)) {
                        console.warn('オプション詳細が配列ではありません:', details);
                        return (
                          <Button 
                            variant="outlined" 
                            onClick={() => setSelectedOrder({
                              ...selectedOrder, 
                              option_details: '[]'
                            })}
                          >
                            オプション詳細を初期化
                          </Button>
                        );
                      }
                      
                      return details.map((detail, index) => (
                        <Grid container spacing={2} key={index} sx={{ mt: 1 }}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="項目"
                              value={(detail && detail.name) || ''}
                              onChange={(e) => {
                                try {
                                  let detailsStr = selectedOrder.option_details || '[]';
                                  
                                  // 無効なデータのチェック
                                  if (typeof detailsStr !== 'string') {
                                    detailsStr = '[]';
                                  } else if (detailsStr.includes('[object Object]') || detailsStr.trim() === '') {
                                    detailsStr = '[]';
                                  }
                                  
                                  const details = JSON.parse(detailsStr);
                                  
                                  if (!Array.isArray(details) || !details[index]) {
                                    // 配列でない、またはインデックスが存在しない場合は初期化
                                    setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: e.target.value, castback: 0, profit: 0 }])});
                                    return;
                                  }
                                  
                                  details[index].name = e.target.value;
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify(details)});
                                } catch (error) {
                                  console.error('オプション詳細の更新中にエラーが発生しました:', error);
                                  // エラー時は新しい配列で初期化
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: e.target.value, castback: 0, profit: 0 }])});
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label="キャストバック"
                              value={(detail && detail.castback) || 0}
                              onChange={(e) => {
                                try {
                                  let detailsStr = selectedOrder.option_details || '[]';
                                  
                                  // 無効なデータのチェック
                                  if (typeof detailsStr !== 'string') {
                                    detailsStr = '[]';
                                  } else if (detailsStr.includes('[object Object]') || detailsStr.trim() === '') {
                                    detailsStr = '[]';
                                  }
                                  
                                  const details = JSON.parse(detailsStr);
                                  
                                  if (!Array.isArray(details) || !details[index]) {
                                    // 配列でない、またはインデックスが存在しない場合は初期化
                                    setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: '', castback: parseInt(e.target.value) || 0, profit: 0 }])});
                                    return;
                                  }
                                  
                                  details[index].castback = parseInt(e.target.value) || 0;
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify(details)});
                                } catch (error) {
                                  console.error('オプション詳細の更新中にエラーが発生しました:', error);
                                  // エラー時は新しい配列で初期化
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: '', castback: parseInt(e.target.value) || 0, profit: 0 }])});
                                }
                              }}
                              InputProps={{
                                startAdornment: <span>¥</span>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label="収益"
                              value={(detail && detail.profit) || 0}
                              onChange={(e) => {
                                try {
                                  let detailsStr = selectedOrder.option_details || '[]';
                                  
                                  // 無効なデータのチェック
                                  if (typeof detailsStr !== 'string') {
                                    detailsStr = '[]';
                                  } else if (detailsStr.includes('[object Object]') || detailsStr.trim() === '') {
                                    detailsStr = '[]';
                                  }
                                  
                                  const details = JSON.parse(detailsStr);
                                  
                                  if (!Array.isArray(details) || !details[index]) {
                                    // 配列でない、またはインデックスが存在しない場合は初期化
                                    setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: '', castback: 0, profit: parseInt(e.target.value) || 0 }])});
                                    return;
                                  }
                                  
                                  details[index].profit = parseInt(e.target.value) || 0;
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify(details)});
                                } catch (error) {
                                  console.error('オプション詳細の更新中にエラーが発生しました:', error);
                                  // エラー時は新しい配列で初期化
                                  setSelectedOrder({...selectedOrder, option_details: JSON.stringify([{ name: '', castback: 0, profit: parseInt(e.target.value) || 0 }])});
                                }
                              }}
                              InputProps={{
                                startAdornment: <span>¥</span>,
                              }}
                            />
                          </Grid>
                        </Grid>
                      ));
                    } catch (error) {
                      console.error('オプション詳細の解析中にエラーが発生しました:', error);
                      // エラー時は即時に新しい値を設定して状態を回復
                      setTimeout(() => {
                        setSelectedOrder(prev => ({
                          ...prev, 
                          option_details: JSON.stringify([{ name: '', castback: 0, profit: 0 }])
                        }));
                      }, 0);
                      
                      return (
                        <Button 
                          variant="outlined" 
                          onClick={() => setSelectedOrder({
                            ...selectedOrder, 
                            option_details: JSON.stringify([{ name: '', castback: 0, profit: 0 }])
                          })}
                        >
                          オプション詳細を再設定
                        </Button>
                      );
                    }
                  })()
                ) : (
                  <Button 
                    variant="outlined" 
                    onClick={() => setSelectedOrder({
                      ...selectedOrder, 
                      option_details: JSON.stringify([{ name: '', castback: 0, profit: 0 }])
                    })}
                  >
                    オプション詳細を追加
                  </Button>
                )}
                {selectedOrder.option_details && (
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 1 }}
                    onClick={() => {
                      try {
                        let detailsStr = selectedOrder.option_details || '[]';
                        
                        // 無効なデータのチェック
                        if (typeof detailsStr !== 'string') {
                          detailsStr = '[]';
                        } else if (detailsStr.includes('[object Object]') || detailsStr.trim() === '') {
                          detailsStr = '[]';
                        }
                        
                        let details = [];
                        try {
                          details = JSON.parse(detailsStr);
                        } catch (parseError) {
                          console.error('オプション詳細の解析に失敗しました。新しい配列を作成します:', parseError);
                          details = [];
                        }
                        
                        // 配列でない場合は新しい配列を作成
                        if (!Array.isArray(details)) {
                          console.warn('オプション詳細が配列ではありません。新しい配列を作成します:', details);
                          details = [];
                        }
                        
                        details.push({ name: '', castback: 0, profit: 0 });
                        setSelectedOrder({...selectedOrder, option_details: JSON.stringify(details)});
                      } catch (error) {
                        console.error('オプション詳細の追加中にエラーが発生しました:', error);
                        // エラー時は新しい配列で初期化
                        setSelectedOrder({
                          ...selectedOrder, 
                          option_details: JSON.stringify([{ name: '', castback: 0, profit: 0 }])
                        });
                      }
                    }}
                  >
                    項目を追加
                  </Button>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="特記事項"
                  value={selectedOrder.special_instructions || ''}
                  onChange={(e) => setSelectedOrder({...selectedOrder, special_instructions: e.target.value})}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setSelectedOrder(null)} 
              color="error"
            >
              キャンセル
            </Button>
            {!isCastUser && (
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => handleDeleteOrder(selectedOrder.id)}
                sx={{ mr: 'auto' }}
              >
                削除
              </Button>
            )}
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => handleUpdateOrder(selectedOrder)}
            >
              更新
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );

  const handleRetryFetch = () => {
    setError(null);
    if (selectedDate) {
      fetchOrders(selectedDate);
    }
  };

  return (
    <DashboardLayout title="オーダー管理">
      {renderContent()}
      {renderDialogs()}
    </DashboardLayout>
  );
};

export default OrderManagement;
